import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  ColorSetting,
  EditorGroup,
  NumberSetting,
  PageEditorShell,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

function pageValue(form) {
  return form?.catalog?.productPage || {};
}

function ProductPagePreview({ form }) {
  const page = pageValue(form);
  const theme = form?.theme || {};
  const textColor = page.textColor || theme.textColor || "#1A1A2E";
  const mutedText = page.mutedTextColor || theme.mutedTextColor || "#5B5D73";
  const background = page.backgroundColor || theme.backgroundColor || "#EEF0F5";
  const surface = page.surfaceColor || theme.surfaceColor || "#FFFFFF";
  const split = page.layout !== "stacked";
  const collapsedLines = Math.max(0, Number(page.descriptionCollapsedLines) || 0);

  return (
    <Box sx={{ minHeight: 720, bgcolor: background, color: textColor, px: { xs: 1.5, md: 4 }, pt: { xs: 7, md: 8 }, pb: 5 }}>
      {page.showBreadcrumbs !== false ? (
        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 2.2, color: mutedText }}>
          <Typography variant="body2">{page.homeLabel || form?.navigation?.homeLabel || "Inicio"}</Typography>
          <Typography variant="body2">/</Typography>
          <Typography variant="body2">{page.catalogLabel || form?.navigation?.productsLabel || "Catálogo"}</Typography>
          <Typography variant="body2">/</Typography>
          <Typography variant="body2" sx={{ color: textColor, fontWeight: 800 }}>Producto de ejemplo</Typography>
        </Stack>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: split ? { xs: "1fr", md: "minmax(0,1.15fr) minmax(330px,.85fr)" } : "1fr",
          gap: 3,
          alignItems: "start",
        }}
      >
        <Paper sx={{ p: 1.5, bgcolor: surface, borderRadius: 3 }}>
          <Box sx={{ height: split ? { xs: 300, md: 460 } : 360, borderRadius: 2, bgcolor: "rgba(25,36,47,.08)", display: "grid", placeItems: "center", overflow: "hidden" }}>
            <Typography sx={{ color: mutedText, fontWeight: 850 }}>Imagen del producto · {page.imageFit || "contain"}</Typography>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, bgcolor: surface, borderRadius: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip label="Producto" color="primary" />
            <Chip label="Categoría" variant="outlined" />
          </Stack>
          <Typography variant="h3" sx={{ fontWeight: 950, color: textColor }}>Producto de ejemplo</Typography>
          <Typography variant="h4" sx={{ mt: 1.2, color: theme.secondaryColor || "#19242F", fontWeight: 900 }}>$ 25.000</Typography>
          <Divider sx={{ my: 2.2 }} />
          <Typography
            sx={{
              color: mutedText,
              lineHeight: 1.75,
              ...(collapsedLines > 0 ? {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: collapsedLines,
                overflow: "hidden",
              } : {}),
            }}
          >
            Descripción de ejemplo para previsualizar la página de detalle. Esta sección conserva el contenido real del producto y permite decidir si se muestra completo o colapsado.
          </Typography>
          {collapsedLines > 0 ? (
            <Button size="small" sx={{ mt: 0.8, px: 0 }}>{page.descriptionMoreLabel || "Ver más"}</Button>
          ) : null}

          {page.showCapabilities !== false ? (
            <Paper variant="outlined" sx={{ mt: 2.5, p: 1.8, bgcolor: background, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 900 }}>Características operativas</Typography>
              <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>Entrega, reserva o modalidad aparecen cuando corresponda al ítem.</Typography>
            </Paper>
          ) : null}

          {page.showStock !== false ? (
            <Typography sx={{ mt: 2.2, fontWeight: 900, color: "success.main" }}>8 unidades disponibles</Typography>
          ) : null}

          {page.showQuickCart !== false ? (
            <Button fullWidth variant="contained" startIcon={<ShoppingCartRoundedIcon />} sx={{ mt: 2 }}>
              Agregar al carrito
            </Button>
          ) : null}

          {page.showAttributes !== false ? (
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontWeight: 900 }}>{page.additionalInfoTitle || "Información adicional"}</Typography>
              <Typography variant="body2" sx={{ mt: 0.7, color: mutedText }}>Material · Ejemplo</Typography>
            </Box>
          ) : null}

          {page.showWhatsapp !== false ? (
            <Button fullWidth variant="outlined" startIcon={<WhatsAppIcon />} sx={{ mt: 2 }}>
              Consultar por WhatsApp
            </Button>
          ) : null}
        </Paper>
      </Box>

      <Button startIcon={<ArrowBackRoundedIcon />} sx={{ mt: 3 }}>
        {page.backLabel || "Volver al catálogo"}
      </Button>
    </Box>
  );
}

export default function ProductPageEditor({ form, device, saving = false, onCommit }) {
  const page = pageValue(form);
  const set = React.useCallback((key, value) => onCommit?.(`catalog.productPage.${key}`, value), [onCommit]);

  return (
    <PageEditorShell
      title="Detalle de producto"
      description="Edita /producto/:id usando SiteSettings y sin tocar el contrato de los productos."
      device={device}
      saving={saving}
      preview={<ProductPagePreview form={form} />}
    >
      <EditorGroup title="Navegación y textos">
        <TextSetting label="Volver al catálogo" value={page.backLabel} onChange={(value) => set("backLabel", value)} />
        <TextSetting label="Breadcrumb Inicio" value={page.homeLabel} onChange={(value) => set("homeLabel", value)} helperText="Vacío = heredar navigation.homeLabel." />
        <TextSetting label="Breadcrumb Catálogo" value={page.catalogLabel} onChange={(value) => set("catalogLabel", value)} helperText="Vacío = heredar navigation.productsLabel." />
        <TextSetting label="Título de información adicional" value={page.additionalInfoTitle} onChange={(value) => set("additionalInfoTitle", value)} />
        <ToggleSetting label="Mostrar breadcrumbs" checked={page.showBreadcrumbs} onChange={(value) => set("showBreadcrumbs", value)} />
      </EditorGroup>

      <EditorGroup title="Contenido y acciones">
        <ToggleSetting label="Mostrar stock" checked={page.showStock} onChange={(value) => set("showStock", value)} />
        <ToggleSetting label="Mostrar atributos" checked={page.showAttributes} onChange={(value) => set("showAttributes", value)} />
        <ToggleSetting label="Mostrar capacidades" checked={page.showCapabilities} onChange={(value) => set("showCapabilities", value)} helperText="Duración, modalidad, entrega y datos equivalentes cuando correspondan." />
        <ToggleSetting label="Mostrar compra rápida / carrito" checked={page.showQuickCart} onChange={(value) => set("showQuickCart", value)} />
        <ToggleSetting label="Mostrar WhatsApp" checked={page.showWhatsapp} onChange={(value) => set("showWhatsapp", value)} />
      </EditorGroup>

      <EditorGroup title="Descripción">
        <NumberSetting
          label="Líneas antes de colapsar"
          value={page.descriptionCollapsedLines}
          min={0}
          max={12}
          onChange={(value) => set("descriptionCollapsedLines", value)}
          helperText="0 mantiene la descripción completa como hasta ahora."
        />
        <TextSetting label="Ver más" value={page.descriptionMoreLabel} onChange={(value) => set("descriptionMoreLabel", value)} />
        <TextSetting label="Ver menos" value={page.descriptionLessLabel} onChange={(value) => set("descriptionLessLabel", value)} />
      </EditorGroup>

      <EditorGroup title="Presentación">
        <SelectSetting
          label="Layout"
          value={page.layout || "split"}
          onChange={(value) => set("layout", value)}
          options={[
            { value: "split", label: "Imagen + detalle" },
            { value: "stacked", label: "Apilado" },
          ]}
        />
        <SelectSetting
          label="Ajuste de imagen"
          value={page.imageFit || "contain"}
          onChange={(value) => set("imageFit", value)}
          options={[
            { value: "contain", label: "Contener" },
            { value: "cover", label: "Cubrir" },
          ]}
        />
        <ColorSetting label="Fondo de página" value={page.backgroundColor} onChange={(value) => set("backgroundColor", value)} />
        <ColorSetting label="Superficies" value={page.surfaceColor} onChange={(value) => set("surfaceColor", value)} />
        <ColorSetting label="Texto" value={page.textColor} onChange={(value) => set("textColor", value)} />
        <ColorSetting label="Texto secundario" value={page.mutedTextColor} onChange={(value) => set("mutedTextColor", value)} />
      </EditorGroup>
    </PageEditorShell>
  );
}
